import { Button } from "@/components/ui/baseUi/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { PayoutDetailsEntity } from "@modules/finances/financesPayout/entities/payoutEntity"
import { PayoutEnumApi } from "@modules/finances/financesPayout/gateway/payoutApi"
import { downloadFile } from "@modules/utils/downloadFile"
import { LoggerType } from "@modules/utils/loggerUtils"
import * as Sentry from "@sentry/react"
import { Download, Loader2 } from "lucide-react"
import { useCallback, useImperativeHandle, useState } from "react"

export type PayoutDetailsDownloadModalRef = {
  open: () => void
}

type Props = {
  payoutDetails: PayoutDetailsEntity
  payoutEnumApi: PayoutEnumApi
  ref: React.Ref<PayoutDetailsDownloadModalRef>
}

export function PayoutDetailsDownloadModal({ payoutDetails, payoutEnumApi, ref }: Props) {
  const [showModal, setShowModal] = useState(false)
  const [csvLoadingStatus, setCsvLoadingStatus] = useState<"idle" | "pending" | "downloading" | "success" | "error">(
    "idle",
  )
  // const [isLoadingPdf, setIsLoadingPdf] = useState(false)
  const { userFinanceApi, boxFinanceApi } = useServicesContext()
  const financeApi = payoutEnumApi === PayoutEnumApi.OWNER ? boxFinanceApi : userFinanceApi

  const onHide = useCallback(() => {
    setShowModal(false)
  }, [])

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  const downloadCsv = async () => {
    try {
      setCsvLoadingStatus("pending")
      const result = await financeApi.exportPayoutTransactions(payoutDetails.id)
      setCsvLoadingStatus("downloading")
      downloadFile("transaction.csv", result, "text/csv")
      toast.success(word("finances.page.export.success"), { classNames: successToastClassName() })
      onHide()
      setCsvLoadingStatus("success")
    } catch (e: any) {
      setCsvLoadingStatus("error")
      Sentry.captureException(`${LoggerType.Financial} Export csv data in payout ${e.message}`)
      toast.error(e.message, { classNames: errorToastClassName() })
    }
  }

  return (
    <Dialog open={showModal} onOpenChange={setShowModal}>
      <DialogContent className="w-[510px]">
        <DialogHeader>
          <DialogTitle>
            <Text
              fontStyle="TL"
              className="mt-4 flex content-center justify-center pt-4 text-center text-textDefault-heading"
            >
              {word("global.download")}
            </Text>
          </DialogTitle>
          <DialogDescription>
            <Text fontStyle="BM" className="text-textDefault-caption">
              {word("finances.payoutDownloadModal.csvText")}
            </Text>
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6 flex flex-col gap-2">
          <Button
            variant="primary"
            disabled={csvLoadingStatus === "pending" || csvLoadingStatus === "downloading"}
            onClick={() => downloadCsv()}
          >
            {csvLoadingStatus === "pending"
              ? word("finances.payoutDownloadModal.generationInProgress")
              : csvLoadingStatus === "downloading"
                ? word("finances.payoutDownloadModal.downloadInProgress")
                : word("finances.payoutDownloadModal.csvDownload")}
            {(csvLoadingStatus === "pending" || csvLoadingStatus === "downloading") && (
              <Loader2 size={15} className="animate-spin" />
            )}
            <Download size={15} />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
